/* eslint-disable max-lines-per-function -- TODO: refactor this component */
import { Permissions, RefreshTokenResult, UserProfile } from "@kinde/js-utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { FeatureFlags, IUserRoles, useUserInfo } from "@next-platform/client-common-core";
import { KINDE_ORG } from "@next-platform/client-common-core/infra/constants";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { userIdUtils } from "@next-platform/client-common-core/services/auth";
import { ApplicationIds } from "@next-platform/packages-ts-types";
import type { AxiosError } from "axios";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { JWT_LOCAL_STORAGE_KEY } from "@/constants";
import { IClaims, IDecodedUser, IExternalAuthContext, IExternalAuthProviderProps, IFeatureFlags, ILoginParams } from "@/types";
import decodeJwtClaims from "@/utils/decodeJwtClaims";

import jsonApiService from "./jsonApiService";

const HTTP_STATUS_UNAUTHORIZED = 401;
const TOKEN_RENEWAL_THRESHOLD_RATIO = 0.9;
const LOCAL_PARTNER_REDIRECT_URL = "http://localhost:5173/";

const logger = appLogger.withTag("ExternalAuthProvider");

const defaultPermissions: Permissions<string> = {
  permissions: [],
  orgCode: "",
};

export const ExternalAuthContext = createContext<IExternalAuthContext>({
  isAuthenticated: false,
  isLoading: false,
  getToken: async () => undefined,
  logout: async () => {},
  login: async () => {},
  register: async () => {},
  permissions: [],
  user: null,
  isAuthenticationRequired: false,
  kindeGetPermissions: async () => defaultPermissions,
  kindeGetClaim: async (key: string) => ({ name: key, value: [] }),
  kindeRefreshToken: async (arg: { clientId: string; domain: string }): Promise<RefreshTokenResult> => arg as unknown as Promise<RefreshTokenResult>,
  saveToken: () => false,
});

export const ExternalAuthProvider = ({ children }: IExternalAuthProviderProps) => {
  const refIsPartner = useRef(false);
  const [stateIsLoading, setStateIsLoading] = useState(true);
  const [stateIsAuthenticationRequired, setStateIsAuthenticationRequired] = useState(false);
  const [stateUser, setStateUser] = useState<IDecodedUser | UserProfile | undefined | null>(null);
  const [statePermissions, setStatePermissions] = useState<string[] | null>([]);
  const { setRoles, setUserFeatureFlags, isAuthenticated, setIsAuthenticated, setIsAuthLoaded } = useUserInfo();
  // internal state
  const [stateJwt, setStateJwt] = useState<string | null>(null);

  const isProd = import.meta.env.MODE.includes("production");

  // Get applicationId synchronously from domain/env (memoized - hostname never changes)
  // Use the string value directly to avoid unsafe double cast
  const currentApplicationId = useMemo(
    () => basicAppConfig.applicationId as string as ApplicationIds,
    [], // Empty deps - hostname never changes during component lifecycle
  );

  useEffect(() => {
    const currentUser = stateUser as IDecodedUser;
    if (refIsPartner.current && currentUser?.sub && currentUser?.token && currentUser.id !== currentUser.sub) {
      setStateUser({ ...currentUser, id: currentUser.sub });
      setIsAuthenticated(!!currentUser.token);
    }
  }, [setIsAuthenticated, stateUser]);

  // Kinde states
  const {
    isAuthenticated: isKindeAuthenticated,
    getAccessToken: getKindeToken,
    logout: kindeLogout,
    login: kindeLogin,
    register: register,
    getPermissions: kindeGetPermissions,
    user: kindeUser,
    isLoading: isKindeLoading,
    getClaim: kindeGetClaim,
    getFlag: getKindeFeatureFlag,
    refreshToken: kindeRefreshToken,
  } = useKindeAuth();

  const getFeatureFlagsForUser = useCallback(async () => {
    try {
      const [isTTSEnabled, isGUIV2Enabled] = await Promise.all([getKindeFeatureFlag(FeatureFlags.TTS), getKindeFeatureFlag(FeatureFlags.V2)]);
      return {
        [FeatureFlags.TTS]: !!isTTSEnabled,
        [FeatureFlags.V2]: !!isGUIV2Enabled,
      };
    } catch {
      return {
        [FeatureFlags.TTS]: false,
        [FeatureFlags.V2]: false,
      };
    }
  }, [getKindeFeatureFlag]);

  const saveToken = useCallback(
    (newJwt: string): boolean => {
      const claims = newJwt ? decodeJwtClaims(newJwt) : null;
      if (!newJwt || !claims) {
        // No token, or one we cannot even decode (e.g. a malformed
        // ?access_token=): never persist or apply it. Falling through to this
        // branch instead of throwing keeps a bad handoff from breaking the
        // caller. Also avoids the old bug of writing 'undefined' to storage.
        // Returning `false` lets callers recover (clear poisoned storage, fall
        // back to a refresh) instead of silently treating this as success.
        logger.warn("saveToken refused: token missing or not a decodable JWT");
        setStateIsAuthenticationRequired(true);
        setIsAuthenticated(false);
        setStateIsLoading(false);
        return false;
      }

      setStateIsLoading(true);
      if (refIsPartner.current) {
        if (isProd) sessionStorage.setItem(JWT_LOCAL_STORAGE_KEY, newJwt);
        else localStorage.setItem(JWT_LOCAL_STORAGE_KEY, newJwt);
      }
      setStateJwt(newJwt);

      const isReducedAccessToken = claims.id === claims.external_id;
      const isFromPartner = !!claims.external_id;
      const userToSave = {
        ...claims,
        isFromPartner: isFromPartner,
        token: newJwt,
      } as unknown as IDecodedUser;

      setStateUser(userToSave);
      setStatePermissions((claims.permissions as string[]) ?? null);
      setIsAuthenticated(!isReducedAccessToken);
      setStateIsAuthenticationRequired(false);
      setStateIsLoading(false);
      return true;
    },
    [isProd, setIsAuthenticated],
  );

  const invokeRefreshToken = useCallback(async () => {
    try {
      setStateIsLoading(true);
      const tokens = await jsonApiService.refreshJwt();
      if (!tokens.token) {
        setStateIsAuthenticationRequired(true);
        setIsAuthenticated(false);
      }
      saveToken(tokens.token);
    } catch (e) {
      const error = e as AxiosError;
      setStateIsLoading(false);
      // Error is 401 then Login is required
      if (error.response?.status === HTTP_STATUS_UNAUTHORIZED) {
        setStateIsAuthenticationRequired(true);
        setIsAuthenticated(false);
        // If not 401, we want to fail asap to fix the error
      } else {
        throw e;
      }
    }
  }, [setStateIsAuthenticationRequired, setIsAuthenticated, setStateIsLoading, saveToken]);

  const setNewToken = useCallback(
    (newJwt: string) => {
      // Force to get a refresh token and set refreshToken that might have been missing.
      // we can ignore the result of the tokens as we care about the cookie.
      // Only refresh when the token was actually applied — a refused save must
      // not trigger a refresh against a token we already rejected.
      if (newJwt && saveToken(newJwt)) {
        void jsonApiService.refreshJwt();
      }
    },
    [saveToken],
  );

  useEffect(() => {
    if (!refIsPartner.current) {
      setStateIsLoading(isKindeLoading);
      setIsAuthenticated(isKindeAuthenticated);
      // for SC & PC we set the auth loaded to true when kinde auth loading is done.
      setIsAuthLoaded(!isKindeLoading || isKindeAuthenticated);
    } else {
      // for partner, we set the auth loaded always true.
      setIsAuthLoaded(true);
    }
  }, [isKindeLoading, isKindeAuthenticated, setIsAuthLoaded, setIsAuthenticated]);

  useEffect(() => {
    const retrieveKindeStats = async () => {
      try {
        return await Promise.all([
          kindeGetClaim<IClaims, IUserRoles[]>("roles"),
          kindeGetClaim("external_org_id"),
          getFeatureFlagsForUser(),
          kindeGetPermissions(),
        ]);
      } catch {
        throw new Error("Couldn't authenticate. Try again");
      }
    };

    if (!refIsPartner.current && isKindeAuthenticated) {
      void retrieveKindeStats().then(
        ([kindeRoles, externalOrgClaim, featureFlags, kindePermissions]: [
          { value: IUserRoles[] } | null,
          { value: unknown } | null,
          IFeatureFlags,
          {
            permissions: string[];
          },
        ]) => {
          const roles = kindeRoles?.value;
          const externalOrgId = typeof externalOrgClaim?.value === "string" ? externalOrgClaim.value : undefined;
          const rawUser = kindeUser as UserProfile | null;

          if (!rawUser?.id) {
            setIsAuthenticated(false);
            setStateIsLoading(false);
            setStateUser(null);
            return;
          }

          const normalizedUser = {
            ...rawUser,
            external_org_id: externalOrgId,
            id: userIdUtils.toApplicationUserId({
              rawUserId: rawUser.id,
              applicationId: currentApplicationId,
              namespace: externalOrgId,
            }),
          };

          setUserFeatureFlags(featureFlags);
          if (roles) setRoles(roles);
          setIsAuthenticated(true);
          setStateIsLoading(false);
          setStateUser(normalizedUser);
          setStatePermissions(kindePermissions.permissions);
        },
      );
    }
  }, [
    currentApplicationId,
    isKindeAuthenticated,
    kindeGetClaim,
    kindeGetPermissions,
    kindeUser,
    setIsAuthenticated,
    setRoles,
    setUserFeatureFlags,
    getFeatureFlagsForUser,
  ]);

  const shouldRenewToken = useCallback(() => {
    if (refIsPartner.current && stateUser) {
      const now = new Date();
      const tokenIssuance = Number((stateUser as IDecodedUser).iat);
      const tokenExpiration = Number((stateUser as IDecodedUser).exp);
      const timeLeft = now.getTime() - tokenExpiration;

      // if we are at more than 90% of the life of the token or if it is expired: return true
      return timeLeft < (tokenExpiration - tokenIssuance) * TOKEN_RENEWAL_THRESHOLD_RATIO;
    } else {
      return false;
    }
  }, [stateUser]);

  const getToken = useCallback(async () => {
    if (!refIsPartner.current && isKindeAuthenticated) {
      return getKindeToken();
    } else if (refIsPartner.current && stateJwt) {
      // Ensure that if we are at more than 90% of the life of the token or if it is expired: we renew it
      if (shouldRenewToken() && !stateIsLoading) {
        await invokeRefreshToken();
      }
      return stateJwt;
    }
  }, [isKindeAuthenticated, stateJwt, getKindeToken, shouldRenewToken, stateIsLoading, invokeRefreshToken]);

  const logout = useCallback(async () => {
    if (refIsPartner.current && !isAuthenticated && !stateJwt) {
      return;
    } else if (!refIsPartner.current) {
      await kindeLogout();
    } else if (refIsPartner.current && stateJwt) {
      sessionStorage.removeItem(JWT_LOCAL_STORAGE_KEY);
      setIsAuthenticated(false);
      setStateIsLoading(false);
      setStateUser(null);
      setStateJwt(null);
      setStatePermissions(null);
      if (!isProd) {
        const backURL = localStorage.getItem("backFromExternal_URL");
        sessionStorage.clear();
        localStorage.clear();
        window.location.assign(backURL || LOCAL_PARTNER_REDIRECT_URL);
      }
    }
  }, [isAuthenticated, stateJwt, kindeLogout, isProd, setIsAuthenticated]);

  const handlePartnerLogin = useCallback(
    async (token?: string | null) => {
      if (token && token.length > 0) {
        setNewToken(token);
        return;
      }

      const localSessionJwt = isProd ? sessionStorage.getItem(JWT_LOCAL_STORAGE_KEY) : localStorage.getItem(JWT_LOCAL_STORAGE_KEY);
      if (localSessionJwt) {
        // Probe purely first: calling saveToken on a corrupt value trips
        // isAuthenticationRequired, which the app.tsx effect turns into an
        // immediate logoutDomain redirect — bouncing the user before the
        // refresh below can recover. A non-null decode guarantees saveToken
        // will succeed, so this faithfully predicts the outcome without
        // mutating auth state.
        if (decodeJwtClaims(localSessionJwt)) {
          saveToken(localSessionJwt);
          return;
        }

        // Corrupt stored value: clear the poisoned entry so it cannot re-fail
        // on every reload, then fall through to a refresh.
        if (isProd) sessionStorage.removeItem(JWT_LOCAL_STORAGE_KEY);
        else localStorage.removeItem(JWT_LOCAL_STORAGE_KEY);
      }

      await invokeRefreshToken();
    },
    [invokeRefreshToken, isProd, saveToken, setNewToken],
  );

  const login = useCallback(
    async ({ isPartner: isAPartner, token, redirectURL, authUrlParams }: ILoginParams) => {
      if (isAPartner) {
        refIsPartner.current = isAPartner;
        await handlePartnerLogin(token);
        return;
      }

      if (authUrlParams?.connectionId) {
        await register({
          ...authUrlParams,
          orgCode: KINDE_ORG,
          state: authUrlParams?.state ? { id: authUrlParams.state } : undefined,
        });
      } else {
        await kindeLogin({
          redirectURL,
          orgCode: KINDE_ORG,
        });
      }
    },
    [handlePartnerLogin, register, kindeLogin],
  ); // We don't need other dependencies

  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isLoading: stateIsLoading,
      getToken,
      saveToken,
      register,
      setIsAuthenticated,
      permissions: statePermissions,
      user: stateUser,
      isAuthenticationRequired: stateIsAuthenticationRequired,
      kindeGetPermissions,
      kindeGetClaim,
      kindeRefreshToken,
      login,
      logout,
    }),
    [
      getToken,
      saveToken,
      isAuthenticated,
      setIsAuthenticated,
      stateIsAuthenticationRequired,
      stateIsLoading,
      statePermissions,
      register,
      stateUser,
      kindeGetPermissions,
      kindeGetClaim,
      kindeRefreshToken,
      login,
      logout,
    ],
  );

  return <ExternalAuthContext.Provider value={contextValue}>{children}</ExternalAuthContext.Provider>;
};
